import { useContext } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import LandingImageCtaPage from '../../../components/landing/LandingImageCtaPage';
import { ModalContext } from '../../../contexts/ModalContext';

type CategoryType = 'sleep' | 'stress' | 'focus' | 'diet';

const CATEGORY_ALIAS_MAP: Record<string, CategoryType> = {
  sleep: 'sleep',
  stress: 'stress',
  focus: 'focus',
  diet: 'diet',
};

const CATEGORY_CONFIG: Record<
  CategoryType,
  {
    imageFile: string;
    deepLinkUri: string;
    audioFile: string;
  }
> = {
  sleep: {
    imageFile: 'sleep.jpg',
    deepLinkUri: 'https://limh6.app.link/iSDCHnnOP1b',
    audioFile: 'sleep_수면유도_51_Cmajor_60bpm_Delta_4hz.mp3',
  },
  stress: {
    imageFile: 'stress.jpg',
    deepLinkUri: 'https://limh6.app.link/ViYSDRpOP1b',
    audioFile: 'stress_스트레스 완화_195_Dmajor_69bpm.mp3',
  },
  focus: {
    imageFile: 'focus.jpg',
    deepLinkUri: 'https://limh6.app.link/2i7sVJrOP1b',
    audioFile: 'focus_집중력 향상_128_Fmajor_60bpm.mp3',
  },
  diet: {
    imageFile: 'diet.jpg',
    deepLinkUri: 'https://forms.gle/faWFuWqQAPDMwgmq8',
    audioFile: 'diet_다이어트_식욕억제 23_Eminor_60bpm.mp3',
  },
};

const DEFAULT_BUTTON_LABEL = '자세히 보기';
const DIET_BUTTON_LABEL = '더 자세히 알아보기';
const DIET_MODAL_CLOSE_LABEL = '닫기';
const DIET_MODAL_NOTIFY_LABEL = '알림 받기';

function CategoryDietModal({ onClose, onClickNotify }: { onClose: () => void; onClickNotify: () => void }) {
  return (
    <div className="w-[328px] rounded-[12px] bg-[#191F28] text-white p-8 flex flex-col gap-4">
      <div className="text-center text-[22px] font-bold leading-[130%]">☺️ 출시를 준비하고 있어요.</div>
      <p className="text-center text-[15px] text-[#d1d6db]">
        관심 가져주셔서 감사합니다.
        <br />
        앱이 출시 되면 알림을 보내드릴게요.
      </p>
      <div className="flex w-full gap-3 mt-3">
        <button
          type="button"
          className="flex-1 h-12 rounded-lg border border-[#242E55] text-[#d1d6db]"
          onClick={onClose}
        >
          {DIET_MODAL_CLOSE_LABEL}
        </button>
        <button
          type="button"
          className="flex-1 h-12 rounded-lg bg-[#4F68FC] text-white font-semibold"
          onClick={onClickNotify}
        >
          {DIET_MODAL_NOTIFY_LABEL}
        </button>
      </div>
    </div>
  );
}

export default function CategoryLandingPage() {
  const { handleModal } = useContext(ModalContext);
  const { type } = useParams<{ type: string }>();
  const requestedType = (type ?? '').toLowerCase();
  const normalizedType = CATEGORY_ALIAS_MAP[requestedType];

  if (!normalizedType) {
    return <Navigate to="/landing/category/sleep" replace />;
  }

  const categoryConfig = CATEGORY_CONFIG[normalizedType];
  const imageFile = categoryConfig.imageFile;
  const categoryDeepLinkUri = categoryConfig.deepLinkUri;
  const isDiet = normalizedType === 'diet';
  const backgroundAudioSrc = isDiet ? undefined : encodeURI(`/landing/category/${categoryConfig.audioFile}`);
  const buttonLabel = isDiet ? DIET_BUTTON_LABEL : DEFAULT_BUTTON_LABEL;

  return (
    <LandingImageCtaPage
      pageName={`LANDING_CATEGORY_${normalizedType.toUpperCase()}`}
      buttonLabel={buttonLabel}
      deepLinkUri={categoryDeepLinkUri}
      imageSrc={`/landing/category/${imageFile}`}
      imageAlt={`${normalizedType} 카테고리 랜딩 이미지`}
      showImageLoadingIndicator
      imageLoadingMessage="이미지를 불러오는 중입니다..."
      backgroundAudioSrc={backgroundAudioSrc}
      audioTitle={`${normalizedType.toUpperCase()} BGM`}
      autoPlayAudio={!isDiet}
      renderFloatingButton={
        isDiet
          ? ({ buttonLabel: renderLabel, onTrackButtonClick, floatingButtonStyle }) => (
              <button
                type="button"
                className="landing-floating-btn landing-floating-btn-orange"
                style={floatingButtonStyle ? { ...floatingButtonStyle, gap: 8 } : { gap: 8 }}
                onClick={() => {
                  onTrackButtonClick();
                  handleModal(
                    <CategoryDietModal
                      onClose={() => {
                        onTrackButtonClick(DIET_MODAL_CLOSE_LABEL);
                        handleModal();
                      }}
                      onClickNotify={() => {
                        onTrackButtonClick(DIET_MODAL_NOTIFY_LABEL);
                        handleModal();
                        window.location.href = categoryDeepLinkUri;
                      }}
                    />
                  );
                }}
              >
                <span>{renderLabel}</span>
                <img
                  src="/landing/category/ArrowRight.svg"
                  alt=""
                  aria-hidden="true"
                  className="landing-category-btn-arrow"
                />
              </button>
            )
          : undefined
      }
    />
  );
}
